import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseCalendar from '../../components/misc/BaseCalendar.jsx';
import BaseCalendarWeek from '../../components/misc/BaseCalendarWeek.jsx';
import stones from '../../resources/images/stones.png';
import BaseButton from '../../components/form/BaseButton.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import IconSearch from '../../components/images/IconSearch.jsx';
import { useLazyGetCalendarLoadQuery, useLazyGetCalendarMeetingsQuery } from '../../api/calendar.js';
import dayjs from 'dayjs';
import { convertDateAndTimeToUTCDateTime, convertToUrlSearchParams } from '../../utils/helper.js';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../api/api.js';
import { TAG_MEETING } from '../../api/tags.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { PERMISSION_CALENDAR } from '../../utils/constants.js';
import { formatStatus, getStatusColor } from '../../utils/calendar.js';

const Calendar = () => {
    const { user, permissions: userPermissions } = useCurrentUser();
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [meetingsLoadData, setMeetingsLoadData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [meetings, setMeetings] = useState([]);
    const [isWeekView, setIsWeekView] = useState(false);

    const toggleView = () => {
        setIsWeekView((prev) => !prev);
    };

    const changeDate = (value) => {
        setSelectedDate(value);
    };

    const handleAdd = () => {
        let selectedCalendarDate = dayjs(selectedDate).format('YYYY-MM-DD');
        let today = dayjs().format('YYYY-MM-DD');
        const date = selectedCalendarDate >= today ? selectedCalendarDate : today;

        navigate(`/calendar/add?${convertToUrlSearchParams({ date })}`);
    };

    const loadRequestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];
        return {
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            private: privateOrg
        };
    }, [organizationSwitch, user?.id]);

    const [loadData] = useLazyGetCalendarLoadQuery();

    const getLoadData = useCallback(async () => {
        if (loadRequestParams.private || loadRequestParams.organizations?.length > 0) {
            const result = await loadData(loadRequestParams);
            if (result?.data?.response?.dates) {
                setMeetingsLoadData(result.data.response.dates);
            }
        }
    }, [loadData, loadRequestParams]);

    const [calendarMeetings] = useLazyGetCalendarMeetingsQuery();

    const getCalendarMeetings = useCallback(async () => {
        if (loadRequestParams.private || loadRequestParams.organizations?.length > 0) {
            const result = await calendarMeetings({
                date: convertDateAndTimeToUTCDateTime(selectedDate).toISOString(),
                ...loadRequestParams
            });

            if (result?.data?.response?.meetings) {
                setMeetings(result.data.response.meetings);
            }
        }
    }, [calendarMeetings, loadRequestParams, selectedDate]);

    const goToMeeting = (meeting) => {
        dispatch(api.util.invalidateTags([TAG_MEETING]));
        navigate(`/calendar/view/${meeting.id}`);
    };

    useEffect(() => {
        if (Object.keys(organizationSwitch).length > 0) {
            getLoadData().then();
        }
    }, [getLoadData, organizationSwitch]);

    useEffect(() => {
        if (Object.keys(organizationSwitch).length > 0) {
            getCalendarMeetings().then();
        }
    }, [calendarMeetings, getCalendarMeetings, organizationSwitch]);

    return (
        <AuthLayout title='Calendar' backRoute='/dashboard'>
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8'>
                <div className='my-5 flex items-center justify-between'>
                    <div className='text-theme1-title2 font-bold text-2xl'>My meetings</div>
                    <div className='flex items-center'>
                        <button className='btn-primary px-4 py-2 mr-4 rounded-2xl' onClick={toggleView}>
                            {isWeekView ? 'Month view' : 'Week view'}
                        </button>
                        <span className='cursor-pointer mr-4' onClick={() => navigate('/calendar/advanced-search')}>
                            <IconSearch className='fill-theme1-primary h-10 w-auto' />
                        </span>
                        {!!userPermissions[PERMISSION_CALENDAR]?.create && (
                            <span className='cursor-pointer' onClick={() => handleAdd()}>
                                <IconAdd className='fill-theme1-primary h-8 w-auto' />
                            </span>
                        )}
                    </div>
                </div>
                {isWeekView ? (
                    <BaseCalendarWeek value={selectedDate} setDate={changeDate} loadData={meetingsLoadData} />
                ) : (
                    <BaseCalendar value={selectedDate} setDate={changeDate} loadData={meetingsLoadData} />
                )}
                {!!userPermissions[PERMISSION_CALENDAR]?.create && meetings.length === 0 && (
                    <>
                        {dayjs(selectedDate).format('YYYY-MM-DD') >= dayjs().format('YYYY-MM-DD') ? (
                            <div className='py-4 flex flex-col justify-center items-center mb-4'>
                                <div className='text-theme1-primary2 font-light max-w-80 text-center mt-8 mb-4'>
                                    You have no meetings yet!
                                </div>
                                <img alt='No meetings' src={stones} className='my-4' />
                                <BaseButton
                                    className='btn-red-outline max-w-96 mt-6'
                                    onClick={() =>
                                        navigate(
                                            `/calendar/add?${convertToUrlSearchParams({ date: dayjs(selectedDate).format('YYYY-MM-DD') })}`
                                        )
                                    }
                                    textValue='Add new meeting'
                                />
                            </div>
                        ) : (
                            <div className='pt-4 flex flex-col justify-center items-center'>
                                <div className='text-theme1-cardStroke font-light max-w-80 text-center'>
                                    No meetings!
                                </div>
                                <img alt='No meetings' src={stones} className='my-4' />
                            </div>
                        )}
                    </>
                )}
                {meetings.length > 0 && (
                    <div className='mt-4'>
                        {meetings.map((meeting) => (
                            <div
                                key={meeting.id}
                                className='border-l-4 border-theme1-primary mb-4 px-2 py-1 flex items-center justify-between'
                            >
                                <div>
                                    <span
                                        className={`text-[9px] text-white rounded-md py-1 px-2 font-extrabold ${getStatusColor(meeting.status)}`}
                                    >
                                        {formatStatus(meeting.status)}
                                    </span>
                                    <div
                                        onClick={() => goToMeeting(meeting)}
                                        className='text-theme1-primary font-bold cursor-pointer mt-[2px]'
                                    >
                                        {meeting.title}
                                    </div>
                                    <div className='text-theme1-text2'>
                                        {meeting.country_id
                                            ? `${meeting.country.code}${meeting.location ? `, ${meeting.location}` : ''}`
                                            : meeting.location}
                                    </div>
                                </div>
                                <div className='flex-shrink-0'>
                                    {meeting.all_day && <div className='text-theme1-text1 text-sm'>All day</div>}
                                    {!meeting.all_day && (
                                        <>
                                            <div className='text-theme1-text1 text-sm'>
                                                {meeting.start_time ? dayjs(meeting.start_time).format('HH:mm') : ''}
                                            </div>
                                            <div className='text-theme1-text1 text-sm'>
                                                {meeting.end_time ? dayjs(meeting.end_time).format('HH:mm') : ''}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AuthLayout>
    );
};

export default Calendar;
